import { useCallback, useEffect, useState } from "react";

export interface ConfigPageResult {
  total: number;
  page: number;
  limit: number;
}

interface UseConfigListPaginationOptions {
  /** When true (default), start in loading until first cache/network resolve. */
  initialLoading?: boolean;
  initialTotal?: number;
}

/**
 * Shared pagination / search / loading state for Config list providers.
 */
export function useConfigListPagination(
  brandKey: string,
  defaultPageSize: number,
  options?: UseConfigListPaginationOptions,
) {
  const [loading, setLoading] = useState(options?.initialLoading ?? true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultPageSize);
  const [total, setTotal] = useState(options?.initialTotal ?? 0);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    setPage(1);
    setSearch("");
    setLimit(defaultPageSize);
  }, [brandKey, defaultPageSize]);

  const handleSearchChange = useCallback((nextSearch: string) => {
    setPage(1);
    setSearch(nextSearch);
  }, []);

  const refetch = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const applyPageResult = useCallback((result: ConfigPageResult) => {
    setTotal(result.total);
    if (result.page > 0) setPage(result.page);
    if (result.limit > 0) setLimit(result.limit);
  }, []);

  const applyLoadError = useCallback(
    (err: unknown, fallbackMessage: string) => {
      setTotal(0);
      setError(err instanceof Error ? err.message : fallbackMessage);
    },
    [],
  );

  return {
    loading,
    setLoading,
    error,
    setError,
    search,
    setSearch: handleSearchChange,
    page,
    setPage,
    limit,
    total,
    reloadToken,
    refetch,
    applyPageResult,
    applyLoadError,
  };
}
